/**
 * Transmission Briefing Component
 * Required interstitial that displays character messages one at a time
 * between narrative and questions in the game flow.
 */

import React, { useState } from 'react';
import { CharacterMessage } from '@/data/gameGuideContent';
import { StageId, STAGES } from '../models/GameState';
import { IoHome, IoJournal, IoCloudOutline, IoEye, IoChatbubble } from 'react-icons/io5';
import { FaBrain } from 'react-icons/fa';

interface TransmissionBriefingProps {
  messages: CharacterMessage[];
  stage: StageId;
  onComplete: () => void;
}

function getIconEmoji(icon: string): React.ReactNode {
  const pngIcons: Record<string, string> = {
    'mothership': '/icons/MOTHERSHIP.png',
    'gee': '/icons/GEE.png',
    'doc': '/icons/doc.png'
  };

  if (pngIcons[icon]) {
    return <img src={pngIcons[icon]} alt={icon} className="w-12 h-12 object-contain" />;
  }

  const iconMap: Record<string, React.ReactNode> = {
    'home': <IoHome className="w-8 h-8" />,
    'journal': <IoJournal className="w-8 h-8" />,
    'memory': <FaBrain className="w-8 h-8" />,
    'conscience': <IoCloudOutline className="w-8 h-8" />,
    'seeing': <IoEye className="w-8 h-8" />
  };
  return iconMap[icon] || <IoChatbubble className="w-8 h-8" />;
}

function getIconLabel(icon: string): string {
  const labelMap: Record<string, string> = {
    'mothership': 'Mothership',
    'home': 'Message from Home',
    'gee': 'GEE',
    'doc': 'DOC',
    'journal': 'Research Journal',
    'memory': 'Memory',
    'conscience': 'Conscience',
    'seeing': 'Observation'
  };
  return labelMap[icon] || icon;
}

export const TransmissionBriefing: React.FC<TransmissionBriefingProps> = ({
  messages,
  stage,
  onComplete
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const stageInfo = STAGES[stage];
  const stageName = stageInfo ? stageInfo.name : `Stage ${stage}`;
  const currentMessage = messages[currentIndex];
  const isLastMessage = currentIndex === messages.length - 1;

  const handleNext = () => {
    if (isLastMessage) {
      onComplete();
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-[#E2E5EC] border-b border-[#CED4DE] px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#12356B] tracking-wide">
            INCOMING TRANSMISSIONS
          </h2>
          <p className="text-sm text-slate-600 mt-0.5">{stageName}</p>
        </div>
        <span className="text-sm font-medium text-[#12356B] bg-white px-3 py-1 rounded-full border border-[#CED4DE]">
          {currentIndex + 1} / {messages.length}
        </span>
      </div>

      {/* Message Content */}
      <div className="px-6 py-6">
        <div className="flex items-start gap-4">
          {/* Character Icon */}
          <div className="flex-shrink-0 w-14 h-14 rounded-full bg-[#E2E5EC] border-2 border-[#CED4DE] flex items-center justify-center text-[#12356B]">
            {getIconEmoji(currentMessage.icon)}
          </div>

          {/* Message */}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-base text-gray-900 mb-2">
              {getIconLabel(currentMessage.icon)}
            </p>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {currentMessage.message}
            </p>
          </div>
        </div>
      </div>

      {/* Progress dots + Action button */}
      <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
        {/* Progress dots */}
        <div className="flex items-center gap-1.5">
          {messages.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex
                  ? 'bg-[#12356B]'
                  : index < currentIndex
                    ? 'bg-[#DD6832]'
                    : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Action button */}
        <button
          onClick={handleNext}
          className="rounded-lg bg-[#367A25] px-5 py-2.5 font-semibold text-white transition-colors hover:bg-[#2d6820]"
        >
          {isLastMessage ? 'Proceed to Mission Decisions' : 'Next Transmission'}
        </button>
      </div>
    </div>
  );
};

export default TransmissionBriefing;
